import React, { useState, useEffect } from 'react';
import { Plus, Edit, Users, Trash2, Key } from 'lucide-react';
import { apiService } from '../../services/api';
import { phoneMask } from '../../utils/masks';

interface Paciente {
  _id: string;
  nome: string;
  email: string;
  telefone?: string;
  tipo: 'paciente';
  createdAt: string;
}

interface ListaPacientesProps {
  onEditPaciente: (id: string) => void;
  onNovoPaciente: () => void;
  onPacienteChange?: () => void;
}

const ListaPacientes: React.FC<ListaPacientesProps> = ({ onEditPaciente, onNovoPaciente, onPacienteChange }) => {
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof Paciente | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    carregarPacientes();
  }, []);

  const carregarPacientes = async () => {
    try {
      const data = await apiService.getPacientes();
      setPacientes(data);
    } catch (error) {
      console.error('Erro ao carregar pacientes:', error);
      // Fallback para dados mock em caso de erro
      const mockPacientes: Paciente[] = [
        {
          _id: '1',
          nome: 'Maria Silva',
          email: 'maria@email.com',
          telefone: '11999999999',
          tipo: 'paciente',
          createdAt: '2024-01-15T10:00:00.000Z'
        },
        {
          _id: '2',
          nome: 'João Santos',
          email: 'joao@email.com',
          telefone: '11888888888',
          tipo: 'paciente',
          createdAt: '2024-01-20T14:30:00.000Z'
        }
      ];
      
      setPacientes(mockPacientes);
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (dataString: string) => {
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR');
  };

  const handleSort = (field: keyof Paciente) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedPacientes = [...pacientes].sort((a, b) => {
    if (!sortField) return 0;
    
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];
    
    if (sortField === 'createdAt') {
      aValue = new Date(aValue as string).getTime();
      bValue = new Date(bValue as string).getTime();
    } else if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleDeletePaciente = async (id: string, nome: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir o paciente ${nome}?`)) {
      return;
    }

    try {
      setDeletingId(id);
      await apiService.deletePaciente(id);
      setPacientes(prev => prev.filter(p => p._id !== id));
      onPacienteChange?.(); // Atualiza contador no dashboard
    } catch (error) {
      console.error('Erro ao deletar paciente:', error);
      alert('Erro ao deletar paciente. Tente novamente.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleResetSenha = async (id: string, nome: string) => {
    if (!window.confirm(`Tem certeza que deseja resetar a senha do paciente ${nome}?`)) {
      return;
    }

    try {
      const primeiroNome = nome.split(' ')[0].toLowerCase();
      const novaSenha = `${primeiroNome}@2025`;
      
      await apiService.updatePaciente(id, { senha: novaSenha });
      alert(`Senha resetada com sucesso! Nova senha: ${novaSenha}`);
    } catch (error) {
      console.error('Erro ao resetar senha:', error);
      alert('Erro ao resetar senha. Tente novamente.');
    }
  };

  const handleWhatsApp = (telefone: string) => {
    if (!telefone) {
      alert('Telefone não cadastrado para este paciente.');
      return;
    }
    
    // Remove todos os caracteres não numéricos
    const numeroLimpo = telefone.replace(/\D/g, '');
    
    if (numeroLimpo.length < 10) {
      alert('Número de telefone inválido.');
      return;
    }
    
    // Detecta se é mobile e usa URL apropriada
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const url = isMobileDevice 
      ? `https://api.whatsapp.com/send?phone=${numeroLimpo}`
      : `https://web.whatsapp.com/send?phone=${numeroLimpo}`;
    
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <output className="spinner-border text-success">
          <span className="visually-hidden">Carregando...</span>
        </output>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="text-success mb-0">Pacientes</h2>
        <button 
          className="btn btn-success d-flex align-items-center gap-2"
          onClick={onNovoPaciente}
        >
          <Plus size={20} />
          Novo Paciente
        </button>
      </div>

      <div className="card shadow-sm">
        <div className="card-body p-0">
          {isMobile ? (
            // Mobile: Cards
            <div className="p-1">
              {sortedPacientes.map((paciente) => (
                <div key={paciente._id} className="card mb-1 border">
                  <div className="card-body" style={{padding: '0.125rem'}}>
                    <h6 className="card-title mb-1 fw-bold">{paciente.nome.toUpperCase()}</h6>
                    <div className="text-muted small" style={{marginBottom: '0.125rem'}}>
                      <strong>Email:</strong> {paciente.email}
                    </div>
                    <div className="text-muted small" style={{marginBottom: '0.125rem'}}>
                      <strong>Telefone:</strong> 
                      {paciente.telefone ? (
                        <button 
                          type="button"
                          className="btn btn-link p-0 text-primary" 
                          style={{textDecoration: 'underline', fontSize: 'inherit'}}
                          onClick={() => handleWhatsApp(paciente.telefone!)}
                          title="Abrir WhatsApp"
                        >
                          {phoneMask(paciente.telefone)}
                        </button>
                      ) : (
                        ' -'
                      )}
                    </div>
                    <div className="text-muted small" style={{marginBottom: '0.25rem'}}>
                      <strong>Cadastro:</strong> {formatarData(paciente.createdAt)}
                    </div>
                    <div className="d-flex gap-1 w-100">
                      <button 
                        className="btn btn-primary btn-sm" 
                        onClick={() => onEditPaciente(paciente._id)}
                        style={{minHeight: '44px', flex: '1'}}
                        title="Editar"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="btn btn-warning btn-sm"
                        onClick={() => handleResetSenha(paciente._id, paciente.nome)}
                        style={{minHeight: '44px', flex: '1'}}
                        title="Resetar Senha"
                      >
                        <Key size={16} />
                      </button>
                      <button 
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeletePaciente(paciente._id, paciente.nome)}
                        disabled={deletingId === paciente._id}
                        style={{minHeight: '44px', flex: '1'}}
                      >
                        {deletingId === paciente._id ? (
                          <output className="spinner-border spinner-border-sm">
                            <span className="visually-hidden">Excluindo...</span>
                          </output>
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            }
            </div>
          ) : (
            // Desktop: Tabela
            <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('nome')}>
                    Nome {sortField === 'nome' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('email')}>
                    Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{textAlign: 'center', cursor: 'pointer'}} onClick={() => handleSort('telefone')}>
                    Telefone {sortField === 'telefone' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{textAlign: 'center', cursor: 'pointer'}} onClick={() => handleSort('createdAt')}>
                    Data Cadastro {sortField === 'createdAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{width: '150px', textAlign: 'center'}}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedPacientes.map((paciente) => (
                  <tr key={paciente._id}>
                    <td className="fw-semibold">{paciente.nome.toUpperCase()}</td>
                    <td className="text-muted">{paciente.email}</td>
                    <td style={{textAlign: 'center'}}>
                      {paciente.telefone ? (
                        <button 
                          type="button"
                          className="btn btn-link p-0 text-primary" 
                          style={{textDecoration: 'underline', fontSize: 'inherit'}}
                          onClick={() => handleWhatsApp(paciente.telefone!)}
                          title="Abrir WhatsApp"
                        >
                          {phoneMask(paciente.telefone)}
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td style={{textAlign: 'center'}}>{formatarData(paciente.createdAt)}</td>
                    <td>
                      <div className="d-flex gap-1 justify-content-center">
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => onEditPaciente(paciente._id)}
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => handleResetSenha(paciente._id, paciente.nome)}
                          title="Resetar Senha"
                        >
                          <Key size={16} />
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeletePaciente(paciente._id, paciente.nome)}
                          disabled={deletingId === paciente._id}
                          title="Excluir"
                        >
                          {deletingId === paciente._id ? (
                            <output className="spinner-border spinner-border-sm">
                              <span className="visually-hidden">Excluindo...</span>
                            </output>
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </div>

      {pacientes.length === 0 && (
        <div className="text-center py-5">
          <div className="text-muted mb-3">
            <Users size={48} />
          </div>
          <h5 className="text-muted">Nenhum paciente cadastrado</h5>
          <p className="text-muted">Clique em "Novo Paciente" para começar</p>
        </div>
      )}
    </div>
  );
};

export default ListaPacientes;